import { TabContext, TabList } from '@mui/lab';
import { Card, CardContent, Chip, Stack, Typography } from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as AIGenerate } from 'common/assets/images/ai-generate.svg';
import { ReactComponent as NoActivityIcon } from 'common/assets/images/NoActivity.svg';
import CardTable from 'common/components/CardTable/CardTable';
import { Loader } from 'common/components/Loader/Loader';
import Tooltip from 'common/components/Tooltip/Tooltip';
import {
	ENTITY_STATUS,
	ENTITY_TYPE,
	RELATION_MAPPING_TAB_VALUES,
	GENERATE_MODE,
} from 'common/constants/TaxonomyConstants';
import { RootState } from 'common/store/AppStore';
import {
	setMappingDrawer,
	setSelectedEntityTypeData,
} from 'common/store/reducer/commonReducer';
import { setTabIndex } from 'common/store/reducer/mappingDetailsReducer';
import {
	hasInventoryManagerRole,
	toSentenceCase,
} from 'common/utils/utilFunctions';

import useTableQuery from 'hooks/useTableQuery';

import { TABLE_SIZE_12 } from 'features/HorizonScanning/components/ActionPlanLayout/ActionPlanLayoutModules';
import StyledTab from 'features/RiskProfile/Taxonomy/StyledTab/StyledTab';

import { getEntitiesCount, getEntityRelationMapping } from '../../api';
import {
	IEntitiesCount,
	IEntityDataCount,
	IRelationMappingCardProps,
	IRelationMappingPayload,
} from '../../api/type-def';
import {
	fetchFilterTypeBasedOnTabValue,
	fetchEntityCountData,
	fetchRelationshipCardBorderStyling,
	containsAIGeneratedCards,
	fetchParamsForFilter,
} from '../../TaxonomyUtils';

import styles from './RelationMappingCard.module.scss';

/* Descructuring of variables */
const { AI_GENERATED } = RELATION_MAPPING_TAB_VALUES;
const { CITATION_GROUP_BU, OBLIGATION, GROUP, AI_GENERATED_ENTITY } =
	ENTITY_TYPE;
const { PENDING, REVIEWED } = ENTITY_STATUS;
const { AI } = GENERATE_MODE;

const {
	textColor,
	tabBorderRadius,
	tabBackground,
	tabSelectedTextColor,
	focustabColor,
} = styles;

/* Local Variables */
const style = {
	'.MuiButtonBase-root': {
		border: 'none',
	},
	'.MuiButtonBase-root.MuiTab-root.Mui-selected': {
		color: tabSelectedTextColor,
	},
	'.Mui-selected': {
		background: `${focustabColor} !important`,
		color: textColor,
		borderRadius: tabBorderRadius,
	},
	'.MuiTabs-flexContainer': {
		backgroundColor: `${tabBackground} !important`,
	},
	'.MuiTabs-indicator': {
		display: 'none',
	},
};

const RelationMappingCard = (props: IRelationMappingCardProps) => {
	/* Descructuring Props */
	const {
		childEntityOptionsData,
		isAddRelationshipModalOpen,
		parentEntityType,
		isSubmitModalOpen,
		isRegenerating,
		setTabValue,
		tabValue,
		entityId,
		entityType,
	} = props;

	const dispatch = useDispatch();

	/* Local states */
	const [isRelationDataLoading, setIsRelationDataLoading] =
		useState<boolean>(true);
	const [relationData, setRelationData] = useState<any[]>([]);
	const [aiCardsData, setAICardsData] = useState<any[]>([]);
	const [releationshipDataCount, setRelationshipDataCount] =
		useState<number>(0);
	const [entityCountObject, setEntityCountObject] = useState<
		IEntityDataCount[]
	>([]);

	/* Store variables */
	const sideDrawerState = useSelector(
		(state: RootState) => state.common.mappingDrawer,
	);
	const loggedInUser: any = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	/* Descructuring variables */
	const { tableQueryObj, updateTableQuery } = useTableQuery({
		id: 'prcRelationshipMappingCard',
		defaultPageSize: TABLE_SIZE_12,
	});
	const { pageNumber } = tableQueryObj;

	const hasInvManagerAccess = hasInventoryManagerRole(loggedInUser);
	const isAITabSelected =
		tabValue?.toLowerCase() === AI_GENERATED?.toLowerCase();

	/**
	 * @description method to trigger when tab value changes
	 * @param event, newValue
	 */
	const tabValueChangeHandler = (
		event: React.SyntheticEvent,
		newValue: string,
	) => {
		event.stopPropagation();
		updateTableQuery({ ...tableQueryObj, pageNumber: 0 });
		setTabValue(newValue);
	};

	/**
	 * @description method to trigger when page number is changed
	 * @param data
	 */
	const handlePageChange = (data: any) => {
		updateTableQuery({ ...tableQueryObj, pageNumber: data?.pageNumber });
	};

	/**
	 * @description method to trigger when relation mapping is clicked
	 * @param rowData
	 */
	const clickMappingCardHandler = useCallback(
		async (rowData: any) => {
			dispatch(setMappingDrawer(true));
			dispatch(setTabIndex('overview'));
			dispatch(
				setSelectedEntityTypeData({
					...rowData,
					type:
						rowData?.childType?.toLowerCase() ===
						CITATION_GROUP_BU.toLowerCase()
							? GROUP
							: rowData?.childType,
					id:
						rowData?.childType?.toLowerCase() ===
						CITATION_GROUP_BU.toLowerCase()
							? rowData?.citationGroupId
							: rowData?.childId,
				}),
			);
		},
		[setMappingDrawer, setSelectedEntityTypeData],
	);

	/**
	 * @description method to fetch relation mapping data
	 * @param pageNumber
	 * @param pageSize
	 */
	const fetchRelationMapping = async (pageNumber: number, tabValue: string) => {
		setIsRelationDataLoading(true);
		const updatedPayload = {
			relationId: entityId,
			relationType: entityType,
		};
		const params: IRelationMappingPayload = fetchParamsForFilter(
			pageNumber,
			fetchFilterTypeBasedOnTabValue(tabValue),
			isAITabSelected ? 'childName' : '',
		);
		const resp: any = await getEntityRelationMapping(updatedPayload, params);
		setIsRelationDataLoading(false);
		if (resp?.responseBody) {
			setRelationData(resp?.responseBody?.content);
			if (isAITabSelected) {
				setAICardsData(resp?.responseBody?.content);
			}
			setRelationshipDataCount(resp?.responseBody?.totalCount);
		}
	};

	/**
	 * @description method to fetch ai relation mapping data
	 * @param pageNumber
	 * @param pageSize
	 */
	const fetchMappingForAICards = async (pageNumber: number) => {
		const params: IRelationMappingPayload = fetchParamsForFilter(
			pageNumber,
			AI_GENERATED_ENTITY,
			'childName',
		);
		const resp: any = await getEntityRelationMapping(
			{
				relationId: entityId,
				relationType: entityType,
			},
			params,
		);
		if (resp?.responseBody) {
			setAICardsData(resp?.responseBody?.content);
			if (isAITabSelected) {
				setRelationshipDataCount(resp?.responseBody?.totalCount);
			}
		}
	};

	/**
	 * @description method to fetch relation mapping data count
	 * @param parentObjectId
	 * @param parentObjectType
	 */
	const fetchEntitiesCount = async (
		parentObjectId: string,
		parentObjectType: string,
	) => {
		const response: any = await getEntitiesCount(
			parentObjectId,
			parentObjectType,
		);
		if (response?.responseBody as IEntitiesCount) {
			const reorderedEntityCount = fetchEntityCountData(
				parentEntityType,
				Number(response?.responseBody?.processCount),
				Number(response?.responseBody?.riskCount),
				Number(response?.responseBody?.controlCount),
				Number(response?.responseBody?.groupCount),
				Number(response?.responseBody?.policyCount),
				Number(response?.responseBody?.aiGeneratedCount),
			);
			setEntityCountObject(reorderedEntityCount);
		}
	};

	/**
	 * @description method to get mapping relationship card data
	 */
	const getRelationMappingCardData = () => (
		<CardTable
			id="prcRelationshipMappingCard"
			handlePagination={handlePageChange}
			defaultQuery={tableQueryObj}
			rows={relationData}
			totalItems={releationshipDataCount}
			render={(rowData) => (
				<Card
					className={`${styles['mapping-card-container']} ${
						styles[
							fetchRelationshipCardBorderStyling(
								rowData?.childType?.toLowerCase() ===
									CITATION_GROUP_BU?.toLowerCase()
									? OBLIGATION
									: rowData?.childType,
							)
						]
					}`}
					data-testid="mapping-card"
					onClick={() => {
						clickMappingCardHandler(rowData);
					}}
				>
					<CardContent className={styles['map-content-card']}>
						<Typography className={styles['card-title']}>
							{rowData?.childType?.toLowerCase() ===
							CITATION_GROUP_BU?.toLowerCase()
								? OBLIGATION
								: rowData?.childType}
							<span className={styles['ai-icons']}>
								{rowData?.generateMode?.toLowerCase() === AI.toLowerCase() ? (
									<AIGenerate />
								) : (
									''
								)}
							</span>
						</Typography>

						{rowData?.childDisplayId && (
							<Typography className={styles['card-display-title']}>
								{rowData?.childDisplayId}
							</Typography>
						)}
						<Tooltip title={rowData?.childName}>
							<Typography className={styles['card-body']}>
								{rowData?.childName}
							</Typography>
						</Tooltip>
						<div className={styles['chip-container']}>
							{rowData?.reviewStatus?.length &&
								(rowData?.reviewStatus?.toLowerCase() ===
									PENDING.toLowerCase() ||
									rowData?.reviewStatus?.toLowerCase() ===
										REVIEWED.toLowerCase()) && (
									<Typography>
										<Chip
											label={rowData?.reviewStatus}
											className={`${
												rowData?.reviewStatus?.toLowerCase() ===
												PENDING?.toLowerCase()
													? styles['pending-review-chip']
													: styles['default-chip']
											}`}
											variant="outlined"
											sx={{ mr: 0.5, mb: 0.5 }}
										/>
									</Typography>
								)}
						</div>
					</CardContent>
				</Card>
			)}
		/>
	);

	/* To fetch relation mapping entity count */
	useEffect(() => {
		fetchEntitiesCount(entityId, entityType);
	}, [isAddRelationshipModalOpen, sideDrawerState, tabValue, isRegenerating]);

	/* To fetch relation mapping data based on dependencies */
	useEffect(() => {
		fetchRelationMapping(pageNumber, tabValue);
		fetchMappingForAICards(pageNumber);
	}, [
		pageNumber,
		isAddRelationshipModalOpen,
		tabValue,
		sideDrawerState,
		isSubmitModalOpen,
		isRegenerating,
	]);

	return (
		<div>
			<Stack className={styles['relation-mapping-card-container']}>
				<TabContext value={tabValue}>
					<TabList
						sx={style}
						onChange={(event: React.SyntheticEvent, value: string) =>
							tabValueChangeHandler(event, value)
						}
						className={styles['panel-layout']}
					>
						{childEntityOptionsData?.map((data: any, index: number) => (
							<StyledTab
								key={data?.value}
								label={
									index !== 0
										? `${data?.label} (${
												entityCountObject[index]?.entityCount || 0
										  })`
										: `${data?.label}`
								}
								data-testid="tab-value"
								value={data?.value}
							/>
						))}
						{containsAIGeneratedCards(aiCardsData) && hasInvManagerAccess && (
							<StyledTab
								data-testid="tab-value"
								value={AI_GENERATED}
								label={`${AI_GENERATED} (${
									entityCountObject[5]?.entityCount || 0
								})`}
							/>
						)}
					</TabList>
				</TabContext>
			</Stack>
			{isRelationDataLoading ? (
				<Loader show className={styles['loader-styling']} />
			) : (
				<div className={styles['mapping-card-section']}>
					{relationData?.length > 0 ? (
						getRelationMappingCardData()
					) : (
						<div className={styles['no-activity-img-wrapper']}>
							<Typography className={styles['no-activity-img']}>
								<NoActivityIcon />
							</Typography>
							<p className={styles['no-data-text']}>
								{toSentenceCase('No Relationship Mapping Found')}
							</p>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default RelationMappingCard;
